import { Component, ElementRef, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { TradeSummary } from '../models/trade-summary';
import { TableColumn, TableColumnType } from '../models/allocation-table';
import _ from 'lodash';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { DialogInputConfig, DialogResult } from 'src/app/shared/models/dialog-input';
import { Trade } from '../models/trade';
import { DialogInputComponent } from 'src/app/shared/components/dialog-input/dialog-input.component';
import { faFilterCircleXmark, faReceipt } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'app-allocation-orders-table',
    templateUrl: './allocation-orders-table.component.html',
    styleUrl: './allocation-orders-table.component.scss',
    standalone: false
})
export class AllocationOrdersTableComponent implements OnInit {
  faFilterCircleXmark: any = faFilterCircleXmark;
  faReceipt: any = faReceipt;

  @Input() data: Trade;
  @Output() onAllocateNextClick = new EventEmitter<TradeSummary>;

  /** All orders (unfiltered) */
  allData: TradeSummary[] = [];
  /** Orders after applying column filters */
  filteredData: TradeSummary[] = [];

  TableColumnType = TableColumnType;

  @ViewChildren('filter', { read: ElementRef }) filters: QueryList<ElementRef>;

  columns: TableColumn[] = [
    {key: 'checkbox', displayName: '', type: TableColumnType.text, isDisplayed: true, isFilterEnabled: false, isBoldText: false},
    {key: 'orderId', displayName: 'Order #', type: TableColumnType.text, isDisplayed: true, isFilterEnabled: true, isBoldText: false},
    {key: 'symbol', displayName: 'Symbol', type: TableColumnType.text, isDisplayed: true, isFilterEnabled: true, isBoldText: false},
    {key: 'side', displayName: 'Side', type: TableColumnType.text, isDisplayed: true, isFilterEnabled: true, isBoldText: false},
    {key: 'execPrice', displayName: 'Price', type: TableColumnType.currency, isDisplayed: true, isFilterEnabled: true, isBoldText: false},
    {key: 'quantity', displayName: 'Quantity', type: TableColumnType.number, isDisplayed: true, isFilterEnabled: true, isBoldText: false},
    {key: 'quantityAllocated', displayName: 'Allocated', type: TableColumnType.number, isDisplayed: true, isFilterEnabled: false, isBoldText: false},
    {key: 'quantityToAllocate', displayName: 'Qty to Allocate', type: TableColumnType.input, isDisplayed: true, isFilterEnabled: false, isBoldText: false},
  ];

  constructor(public dialog: DialogService){}

  ngOnInit(): void {
    this.loadData();
  }

  public loadData() {
    this.allData = this.data.orders;
    this.filteredData = [...this.allData];
  }

  clearFilters(): void {
    this.filters.forEach(input => input.nativeElement.value = '');
    this.applyFilters();
  }

  getFilterValue(column: TableColumn): string {
    const filterEl = this.filters?.find(input => input.nativeElement.id === `${column.key}-filter`);
    let value: string = filterEl ? filterEl.nativeElement.value : '';
    if(value){
      switch(column.type){
        case TableColumnType.currency:
          let valueParts = value.split('.');
          if(valueParts.length === 2 && valueParts[1] === ''){
            value = value.substring(0, value.length - 1);
          }
          return this.formatNumberCurrency(value).replace('.00','');
        case TableColumnType.number:
          return this.formatNumberCurrency(value).replace('$','');
        default:
          return value;
      }
    }
    return value;
  }

  getColumnValue(column: TableColumn, value) {
    if(value){
      switch(column.type){
        case TableColumnType.currency:
          return this.formatNumberCurrency(value).replace('.00','');
        case TableColumnType.number:
          return this.formatNumberCurrency(value).replace('$','');
        default:
          return value;
      }
    }
    return value;
  }

  formatNumberCurrency(number) : string {
    const formattedValue = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number).replace(',','').replace('.00', '');

    return formattedValue;
  }

  // Apply filter on column
  public applyFilters(): void {
    this.filteredData = this.allData.filter(data => {
      return _.every(this.columns, (column: TableColumn) => {
        if (column.isDisplayed && column.isFilterEnabled) {
          let filterValue = this.getFilterValue(column).trim().toLowerCase();
          if (!filterValue) return true;
          switch (column.type) {
            case TableColumnType.currency:
              return this.getColumnValue(column, data[column.key])?.toString().trim().toLowerCase().includes(filterValue);
            case TableColumnType.number:
              return this.getColumnValue(column, data[column.key])?.toString().trim().toLowerCase() === filterValue;
            default:
              return this.getColumnValue(column, data[column.key])?.toString().trim().toLowerCase().includes(filterValue);
          }
        }
        return true;
      });
    });
  }

  public quantityToAllocateChanged(element: TradeSummary, value: number) {
    let totalAccountQuantityAllocated = Math.abs(_.sumBy(element.parent.orders, order =>  order.quantityAllocated ));
    let totalAccountQuantity = Math.abs(element.parent.totalQuantity) - totalAccountQuantityAllocated;

    let enteredValue = Math.abs(value);
    let orderQuantity = Math.abs(element.quantity) - Math.abs(element.allcQuantity);
    let maxValue =  orderQuantity < totalAccountQuantity ? orderQuantity :  totalAccountQuantity;
    let totalAccountQuantityToAllocate = Math.abs(_.sumBy(element.parent.orders, order => { return order.orderId != element.orderId ? order.quantityToAllocate : 0;}));

    let availableQuantityToAllocate = totalAccountQuantityToAllocate < totalAccountQuantity ?
        totalAccountQuantity - totalAccountQuantityToAllocate : 0;
    let maxAvailableValue = maxValue > availableQuantityToAllocate ? availableQuantityToAllocate : maxValue;

    if (totalAccountQuantityToAllocate + enteredValue >= totalAccountQuantity && maxAvailableValue === 0){
        this.openDialog("You don't have enough quantity to allocate to this account!");
        element.quantityToAllocate = element.quantityToAllocate;
        return;
    }

    if (value < 0 || enteredValue > Math.abs(maxAvailableValue) || enteredValue >  Math.abs(element.quantity)) {
        this.openDialog(`The value you entered is out of range (0 - ${maxValue})<br \> Do you want to use the max possible value: ${maxAvailableValue}?`, "Confirm", "Yes", false,
        () => {
            element.quantityToAllocate = maxAvailableValue;
            this.recalculateCalculatedValues(element.parent);
        },
        ()=> {
            element.quantityToAllocate = 0;
        });
    } else {
        this.recalculateCalculatedValues(element.parent);
    }
  }


  private openDialog(text: string, alertTitle: string = 'Alert', confirmText: string = 'Ok', isAlertDialog: boolean = true, onConfirm?: Function, onCancel?: Function) {
    let dialogRef;
    let dialogConfig: DialogInputConfig;
    dialogConfig= {
        inputEnabled: false,
        maxInputLength: 200,
        title: alertTitle,
        confirmText: confirmText,
        isAlertDialog,
        text
    };

    dialogRef = this.dialog.open(DialogInputComponent, {
      data:dialogConfig,
      disableClose: true,
    });


    return dialogRef.afterClosed().subscribe((result: DialogResult) => {
      if(result){
        if(onConfirm){
            onConfirm();
        }
      } else {
        if(onCancel){
            onCancel();
        }
      }
    });
  }

  public getDisplayedColumns(): TableColumn[]{
    return this.columns.filter(column => column.isDisplayed);
  }

  public getDisplayedColumnKeys(): string[]{
    return this.getDisplayedColumns().map(column => column.key);
  }

  public getNonActionDisplayedColumns(): TableColumn[]{
    return this.getDisplayedColumns().filter(column => this.isNonActionColumn(column));
  }

  public isCheckboxAvailable(): boolean{
    return this.columns.find(column => column.isDisplayed && column.key === 'checkbox') != null;
  }

  public getQuantityAllocatedColumn(){
    return this.columns.find(column => column.isDisplayed && column.key === 'quantityAllocated');
  }

  public isQuantityAllocatedAvailable(): boolean{
    return this.getQuantityAllocatedColumn() != null;
  }

  public isOrderFullAllocated(order: TradeSummary): boolean{
    return order.quantity - order.quantityAllocated === 0;
  }

  public getQuantityToAllocateColumn(){
    return this.columns.find(column => column.isDisplayed && column.key === 'quantityToAllocate');
  }

  public isQuantityToAllocateAvailable(): boolean{
    return this.getQuantityToAllocateColumn() != null;
  }

  public isNonActionColumn(column: TableColumn): boolean{
    return column.key !== 'checkbox' && column.key !== 'quantityToAllocate';
  }

  public isAccountFullAllocated(orders: TradeSummary[]): boolean{
    return _.sumBy(orders, (order) => order.quantity - order.quantityAllocated) === 0;
  }

  public isCurrencyColumn(element: TableColumn): boolean {
    return element.type === TableColumnType.currency;
  }

  public checkboxChanged(element, $event: Event) {
    const checked = ($event.target as HTMLInputElement).checked;
    this.recalculateCalculatedValues(element.parent);
    this.recalculateCheckbox(element.parent, checked);
  }

  public parentCheckboxChanged(element) {
    element.orders.forEach((order) => {
        order.checked = element.parentChecked;
    });
    this.recalculateCalculatedValues(element);
    element.ordersChecked = element.parentChecked ? element.orders.length : 0;
    element.partiallyChecked = false;
  }

  private recalculateCalculatedValues(element: Trade) {
    element.quantityCalc = 0;
    element.priceCalc = 0;
    element.orders.forEach((order) => {
        if (order.checked) {
            element.quantityCalc += order.quantityToAllocate;
            element.priceCalc += order.execPrice * order.quantityToAllocate;
        }
    });

    if (element.quantityCalc > 0) {
        element.priceCalc = element.priceCalc / element.quantityCalc;
    }
  }

  private recalculateCheckbox(element, checked: boolean) {
    if (checked) {
        element.ordersChecked++;
    }
    else {
        element.ordersChecked--;
    }

    if (element.ordersChecked !== 0 && element.ordersChecked !== element.orders.length) {
        element.partiallyChecked = true;
        element.parentChecked = false;
    }
    else {
        element.parentChecked = element.ordersChecked === element.orders.length;
        element.partiallyChecked = false;
    }
  }
}
